/**
 * Session Timeout Warning Component
 * Shows a warning dialog when session is about to expire
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';
import { Timer, RefreshCw } from 'lucide-react';
import { useSessionManagement } from '../../hooks/useSessionManagement';

export function SessionTimeoutWarning() {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');

  const { extendSession, getTimeRemainingFormatted, getTimeRemaining } =
    useSessionManagement({
      enabled: true,
      onWarning: () => setShowWarning(true),
      onTimeout: () => {
        setShowWarning(false);
        // Logout is handled by the hook
      },
    });

  useEffect(() => {
    if (!showWarning) return;

    const interval = setInterval(() => {
      const remaining = getTimeRemaining();
      if (remaining <= 0) {
        setShowWarning(false);
        return;
      }
      setTimeRemaining(getTimeRemainingFormatted());
    }, 1000);

    return () => clearInterval(interval);
  }, [showWarning, getTimeRemaining, getTimeRemainingFormatted]);

  const handleExtend = () => {
    extendSession();
    setShowWarning(false);
  };

  const progressValue = () => {
    const remaining = getTimeRemaining();
    const fiveMinutes = 5 * 60 * 1000;
    return ((fiveMinutes - remaining) / fiveMinutes) * 100;
  };

  return (
    <Dialog open={showWarning} disableEscapeKeyDown>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Timer size={24} />
          Session Expiring Soon
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Your session will expire in <strong>{timeRemaining}</strong> due to
          inactivity.
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Click "Stay Logged In" to continue your session, or you will be
          automatically logged out for security purposes.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <LinearProgress variant="determinate" value={progressValue()} color="warning" />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleExtend}
          variant="contained"
          startIcon={<RefreshCw size={16} />}
          fullWidth
        >
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
}
